import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { Navigate, useLocation, useNavigate } from 'react-router-dom';


interface ICompaniesContext {
    recordCount: number
    search: ISearch
    companies: ICompany[]
    isLoading: boolean

    doSearch: (search: ISearch) => void
    handleChangePage: (event: any, page: number) => void
    updateSearch: (newSearch: ISearch) => void
    searchCompanies: (searchRequest: ISearch) => Promise<void>
    deleteCompany: (id: string) => Promise<void>
}

export let companiesContext = React.createContext<ICompaniesContext>({} as ICompaniesContext);

let { Provider } = companiesContext;

interface ICompany {
    companyId: string
    companyName: string
    vatNumber: string
    street: string
    postalCode: string
    city: string
    province: string
    country: string
}

export interface ISearch {
    fullText: string
    pageNr: number
    pageSize: number
}

export let CompaniesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let location = useLocation();
    let navigate = useNavigate();

    let [isLoading, setLoading] = useState(true);


    let [companies, setCompanies] = useState<ICompany[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearch>({
        fullText: '',
        pageNr: 0,
        pageSize: 20
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Navigate to={location.pathname} state={{ search: search }} replace={true} />


    let processState = (state: any) => {
        if (state) {

            setSearch({
                ...search,
                ...state
            });

            searchCompanies({
                ...search,
                ...state
            });
        }
    }

    let doSearch = (values: ISearch) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        navigate(location.pathname, { state: request });
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let searchCompanies = async (searchRequest: ISearch) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/Company/SearchCompanies', searchRequest);

            setCompanies(response.data.items);
            setTotalRecords(response.data.recordCount);
            setSearch({ ...searchRequest });

        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        navigate(location.pathname, { state: request });
    }

    let deleteCompany = async (id: string) => {
        if (window.confirm("Are you sure you want to delete the company?")) {
            try {
                await axios.delete(`/api/Company/DeleteCompany/${id}`)
                setCompanies(companies.filter(company => company.companyId !== id));
                setTotalRecords(recordCount - 1);
                enqueueSnackbar("Company deleted", { variant: 'success' });
            } catch (error) { }
        }
    }


    return (
        <Provider value={{
            recordCount,
            search,
            companies,
            isLoading,

            doSearch,
            handleChangePage,
            updateSearch,
            searchCompanies,
            deleteCompany
        }}>
            {children}
        </Provider>
    );
};